import PropTypes from 'prop-types'
import React from 'react'
import dynamic from 'next/dynamic'
import Layout from '../src/components/layouts'
import DocumentPageLayout from '../src/components/shared/DocumentPageLayout'
import PageRepository from '../src/repositories/PageRepository'
import { getFullUrlFromReq } from '../src/common/serverUtils'
import { generateSeoData } from '../src/common/nextSeoUtils'
import { CONTENT_TYPES } from '../src/common/constants'
import { preferRedirects, serveFileFromUrl } from '../src/common/back/helpers'
import recommended from '../src/common/constants/linking.json'
import logger from '../src/common/logger'
import CustomJsonLd from '../src/components/shared/CustomJsonLd'
import { jsonLdData } from '../src/common/constants/jsonLd'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'
import TrustMarkersCombinationRepository from 'src/repositories/TrustMarkersCombinationRepository'

// Memoize dynamic imports to prevent unnecessary re-imports
const layoutsByType = {
  [CONTENT_TYPES.DOCUMENT_PAGES]: DocumentPageLayout,
  [CONTENT_TYPES.ARTICLE_PAGES]: dynamic(
    () => import('../src/components/layouts/ArticleLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.BLOG_PAGES]: dynamic(
    () => import('../src/components/layouts/BlogLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.DEBT_CONSOLIDATION_STATES_CITIES]: dynamic(
    () => import('../src/components/pages/DebtConsolidationState'),
    { ssr: true }
  ),
  [CONTENT_TYPES.DISCLAIMER_PAGE_LAYOUT]: dynamic(
    () => import('../src/components/shared/DisclaimerPageLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.CLIENT_STORIES]: dynamic(
    () => import('../src/components/shared/ClientStoryLayout'),
    { ssr: true }
  ),
  [CONTENT_TYPES.IFRAMES_PAGES]: dynamic(
    () => import('../src/components/shared/IFramePageLayout'),
    { ssr: true }
  ),
}

// Memoize non-article pages array
const NON_ARTICLE_PAGES = Object.freeze([
  'wwwDisclaimerPages',
  'wwwClientStories',
  'wwwIFramePages',
  'wwwDocumentPages',
])

export const getServerSideProps = preferRedirects(
  async ({ params, req, res, preview }) => {
    const path = `${params.pid?.join('/')}/`
    let trustMarkersCombination = null

    try {
      const pageData = await PageRepository.getInstance().getAllData(
        path,
        preview
      )

      const trustMarkersResponse =
        await TrustMarkersCombinationRepository.getInstance().getModels(
          {},
          preview
        )
      trustMarkersCombination = trustMarkersResponse.find(
        (item) => item.id.toLowerCase() === 'home-variant-8'
      )
      if (trustMarkersCombination) {
        trustMarkersCombination = {
          ...trustMarkersCombination,
          trustMarker: trustMarkersCombination.trustMarker.map(
            ({ metadata, sys, fields }) => ({
              metadata,
              sys,
              fields: {
                ...fields,
                color: '#33b083',
              },
            })
          ),
        }
      }

      // Handle file serving
      if (pageData?.model?.contentType === 'wwwFile') {
        await serveFileFromUrl(res, {
          url: pageData.model.fileAsset?.url,
          contentType: pageData.model.fileAsset?.contentType,
          fileName: pageData.model.fileAsset?.fileName,
        })

        return { props: {} }
      }

      // Early return for invalid pages
      if (
        !pageData?.model?.key ||
        !layoutsByType[pageData?.model?.contentType]
      ) {
        return { notFound: true }
      }

      const locale = pageData?.locale?.toLowerCase() === 'spanish' ? 'es' : 'en'
      const { canonicalUrl } = getFullUrlFromReq(req, params.pid)

      // Process data
      const data = {
        ...pageData.model,
        page: { ...pageData },
        recommendedReads: recommended[path] || {},
        fullPath: path,
        banner: {},
        trustMarkersCombination,
      }

      // Handle geo pages
      if (
        pageData?.path === 'debt-consolidation-near-me/' ||
        data.contentType === 'wwwDebtConsolidationStatesCities'
      ) {
        data.jsonLdId =
          pageData?.path === 'debt-consolidation-near-me/'
            ? 'geo-landing-page'
            : data.city
            ? 'geo-city-page'
            : 'geo-state-page'
      }

      // Generate SEO data
      const seoData = generateSeoData({
        title: data.metaTitle,
        description: data.metaDescription,
        url: canonicalUrl,
        lastUpdate: data.updatedAt,
        follow: data.follow,
        index: data.index,
        images: data.imageAsset
          ? [
              {
                url: data.imageAsset.url,
                width: data.imageAsset.details.image.width,
                height: data.imageAsset.details.image.height,
                alt: data.imageAsset.fileName,
              },
            ]
          : [],
      })

      return {
        props: { data, locale, seoData },
      }
    } catch (error) {
      logger.error('DynamicPage: failed to get serverside props', {
        pid: params.pid,
        path,
        preview,
        error,
      })

      return { notFound: true }
    }
  }
)

const DynamicPage = ({ data, seoData, locale }) => {
  const Component = layoutsByType[data.contentType]
  const showJsonLd = NON_ARTICLE_PAGES.includes(data.contentType)

  return (
    <Layout footerClassName="max-w-1400" locale={locale} isArticleOrBlogPage>
      <WithSeoOverrides {...seoData} />
      {showJsonLd && (
        <CustomJsonLd
          id="webPage"
          data={jsonLdData(
            seoData?.title,
            seoData?.description,
            `/${data?.page?.path}`,
            `${locale}-US`
          )}
        />
      )}
      <Component {...data} locale={locale} />
    </Layout>
  )
}

DynamicPage.propTypes = {
  data: PropTypes.shape({
    contentType: PropTypes.string.isRequired,
    page: PropTypes.shape({
      path: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  locale: PropTypes.oneOf(['en', 'es']).isRequired,
  seoData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
}

export default React.memo(DynamicPage)
